import React, { useState } from 'react';
import { Star } from './KidIcon';
import { pictureFallbackLetter, pictureTint, TINT_CLASS, usablePictureSrc, type PictureTint } from './kidUiHelpers';

/**
 * Shared building blocks of the kid-first design (docs/design/kid-redesign.md, "Style rules").
 * Every screen in the redesign should be built from these instead of one-off class strings.
 */

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(' ');

// ── Button ─────────────────────────────────────────────────────────────────────────────────
// Primary: green, fully rounded, darker bottom edge, white text (5.0:1). One per screen.
// Secondary: card-coloured with a 2–3 px border. Sizes follow the phone/tablet heights.
type ButtonVariant = 'primary' | 'secondary' | 'danger-quiet';
type ButtonSize = 'md' | 'lg' | 'xl';

const BUTTON_SIZE: Record<ButtonSize, string> = {
  md: 'min-h-[48px] px-5 text-base',
  lg: 'min-h-[56px] px-6 text-lg md:min-h-[64px] md:text-xl',
  xl: 'min-h-[64px] px-8 text-xl md:min-h-[80px] md:text-2xl',
};

const BUTTON_VARIANT: Record<ButtonVariant, string> = {
  primary:
    'bg-kid-green hover:bg-kid-green-hover text-white shadow-[0_4px_0_0_#1F5C39] active:translate-y-[3px] active:shadow-[0_1px_0_0_#1F5C39]',
  secondary:
    'bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark border-2 border-kid-border-strong dark:border-kid-border-strong-dark hover:bg-kid-bg dark:hover:bg-kid-bg-dark',
  'danger-quiet':
    'bg-transparent text-kid-text dark:text-kid-text-dark border-2 border-kid-border-strong dark:border-kid-border-strong-dark hover:bg-kid-chip dark:hover:bg-kid-chip-dark',
};

export interface KidButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  icon?: React.ReactNode;     // before the label
  trailing?: React.ReactNode; // after the label (e.g. →)
}

export const KidButton = React.forwardRef<HTMLButtonElement, KidButtonProps>(({
  variant = 'primary', size = 'lg', fullWidth, icon, trailing, className, children, type = 'button', ...rest
}, ref) => (
  <button
    ref={ref}
    type={type}
    className={cx(
      'inline-flex items-center justify-center gap-2 rounded-full font-kid-heading font-extrabold leading-tight transition-colors',
      'focus:outline-none focus-visible:ring-4 focus-visible:ring-kid-green/40',
      'disabled:opacity-50 disabled:pointer-events-none',
      BUTTON_SIZE[size], BUTTON_VARIANT[variant], fullWidth && 'w-full', className,
    )}
    {...rest}
  >
    {icon}
    <span>{children}</span>
    {trailing}
  </button>
));
KidButton.displayName = 'KidButton';

// ── Card ───────────────────────────────────────────────────────────────────────────────────
// 20–28 px radius, 2 px border, no drop shadow. `tone` picks one of the design's surfaces.
type CardTone = 'card' | 'mint' | 'peach' | 'panel';
const CARD_TONE: Record<CardTone, string> = {
  card: 'bg-kid-card dark:bg-kid-card-dark border-kid-border dark:border-kid-border-dark',
  mint: 'bg-kid-green-bg dark:bg-green-950/40 border-transparent',
  peach: 'bg-kid-peach dark:bg-amber-950/40 border-transparent',
  panel: 'bg-kid-bg dark:bg-kid-bg-dark border-transparent',
};

export const KidCard: React.FC<React.HTMLAttributes<HTMLDivElement> & { tone?: CardTone; padded?: boolean }> = ({
  tone = 'card', padded = true, className, children, ...rest
}) => (
  <div className={cx('rounded-kid-card border-2 text-kid-text dark:text-kid-text-dark', CARD_TONE[tone], padded && 'p-4 md:p-6', className)} {...rest}>
    {children}
  </div>
);

// ── Section heading ────────────────────────────────────────────────────────────────────────
// Sentence case, never uppercase (design rule). 20 px phone, 24–28 px tablet.
export const KidSectionHeading: React.FC<React.HTMLAttributes<HTMLHeadingElement>> = ({ className, children, ...rest }) => (
  <h2 className={cx('font-kid-heading font-extrabold text-xl md:text-2xl text-kid-text dark:text-kid-text-dark mb-2', className)} {...rest}>
    {children}
  </h2>
);

// ── Chip ───────────────────────────────────────────────────────────────────────────────────
// Room chip neutral, star chip peach. 28–40 px high, fully rounded.
export const KidChip: React.FC<{ tone?: 'neutral' | 'star' | 'mint'; className?: string; children: React.ReactNode }> = ({
  tone = 'neutral', className, children,
}) => (
  <span
    className={cx(
      'inline-flex items-center gap-1 rounded-full min-h-[30px] px-3 text-sm md:text-base font-bold whitespace-nowrap',
      tone === 'neutral' && 'bg-kid-chip dark:bg-kid-chip-dark text-kid-text dark:text-kid-text-dark',
      tone === 'star' && 'bg-kid-peach dark:bg-amber-900/50 text-kid-text dark:text-kid-text-dark',
      tone === 'mint' && 'bg-kid-green-bg dark:bg-green-900/40 text-kid-green-shadow dark:text-green-200',
      className,
    )}
  >
    {children}
  </span>
);

/** "⭐ 5 stars" chip. `label` is the already-translated text (e.g. "5 stars"). */
export const StarChip: React.FC<{ label: string; className?: string }> = ({ label, className }) => (
  <KidChip tone="star" className={className}>
    <Star size={16} />
    {label}
  </KidChip>
);

// ── Progress bar ───────────────────────────────────────────────────────────────────────────
// 14–26 px, fully rounded, green (or family orange) on a neutral track.
export const KidProgress: React.FC<{
  value: number; max: number; color?: 'green' | 'family'; size?: 'sm' | 'md' | 'lg'; label?: string; className?: string;
}> = ({ value, max, color = 'green', size = 'md', label, className }) => {
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={Math.min(value, max)}
      aria-label={label}
      className={cx(
        'w-full rounded-full overflow-hidden bg-kid-chip dark:bg-kid-chip-dark',
        size === 'sm' && 'h-3.5', size === 'md' && 'h-[18px]', size === 'lg' && 'h-[26px]', className,
      )}
    >
      <div
        className={cx('h-full rounded-full transition-[width] duration-700', color === 'green' ? 'bg-kid-green dark:bg-kid-green-light' : 'bg-kid-family')}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
};

// ── Picture tile ───────────────────────────────────────────────────────────────────────────
// Pictures are scaled to fit (never cropped) on a tinted tile — right for tool drawings. Photos
// that should fill their frame (task and reward header pictures) pass fit="cover" and are cropped
// to the frame instead of showing bars (2026-10-13, Jens). A missing, failed or non-URL picture
// (e.g. a raw key like "bathroom_spray") shows the fallback: the label's first letter.
export const PictureTile: React.FC<{
  src?: string | null;
  label: string;            // used for alt text and the fallback letter
  size?: number | 'fill';   // px square, or fill the parent
  tint?: PictureTint;       // default: picked from the label so tiles vary
  className?: string;
  rounded?: string;         // tailwind radius class
  fit?: 'contain' | 'cover'; // contain = whole picture (default); cover = fill the frame, crop the edges
}> = ({ src, label, size = 72, tint, className, rounded = 'rounded-kid-tile', fit = 'contain' }) => {
  const [failed, setFailed] = useState(false);
  const usable = usablePictureSrc(src);
  const showImg = usable && !failed;
  const t = tint || pictureTint(label);
  const dims = size === 'fill' ? undefined : { width: size, height: size };
  return (
    <div
      className={cx('shrink-0 flex items-center justify-center overflow-hidden', rounded, TINT_CLASS[t], size === 'fill' && 'w-full h-full', className)}
      style={dims}
    >
      {showImg ? (
        <img src={usable} alt={label} className={`w-full h-full ${fit === 'cover' ? 'object-cover' : 'object-contain'}`} onError={() => setFailed(true)} />
      ) : (
        <span
          aria-label={label}
          className="font-kid-heading font-extrabold text-kid-text/80 select-none"
          style={{ fontSize: size === 'fill' ? 48 : Math.max(16, Math.round((size as number) * 0.42)) }}
        >
          {pictureFallbackLetter(label)}
        </span>
      )}
    </div>
  );
};

// ── Switch ─────────────────────────────────────────────────────────────────────────────────
// On/off toggle for settings rows: 56×32, green when on, with a 44 px+ tap area from its row.
export const KidSwitch: React.FC<{
  checked: boolean; onChange: (next: boolean) => void; label: string; disabled?: boolean; className?: string;
}> = ({ checked, onChange, label, disabled, className }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={cx(
      'relative shrink-0 w-14 h-8 rounded-full transition-colors disabled:opacity-50',
      checked ? 'bg-kid-green' : 'bg-[#8D7B6E] dark:bg-[#6b5d52]',
      className,
    )}
  >
    <span
      aria-hidden
      className={cx('absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow transition-transform', checked && 'translate-x-6')}
    />
  </button>
);
